import React, { useState, useEffect } from 'react';
import { AlertCircle, Eye, EyeOff, Loader, Check, Shield, ArrowRight, CheckCircle, FileText, Building, Briefcase, Users, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function LandingPage({ setUser, API_URL, loadSub, user, currentTheme, onToggleTheme }) {
  const navigate = useNavigate();
  const [authMode, setAuthMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [memoLoading, setMemoLoading] = useState(false);
  const [memoVisible, setMemoVisible] = useState(false);

  const generateMemo = () => {
    if (memoLoading) return;
    setMemoLoading(true);
    setTimeout(() => { setMemoLoading(false); setMemoVisible(true); }, 900);
  };

  useEffect(() => { if (user) navigate('/dashboard'); }, [user, navigate]);

  const handleAuth = async (mode) => {
    if (!email || !password) { setApiError('Veuillez remplir tous les champs'); return; }
    setIsLoading(true); setApiError(null);

    try {
      let endpoint = mode === 'signup' ? '/auth/signup' : '/auth/login';
      let body = { email, password, ...(mode === 'signup' && { companyName }) };

      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Erreur auth');

      localStorage.setItem('user', JSON.stringify(data.user));
      if (data.token) localStorage.setItem('token', data.token);
      setUser(data.user);

      if (mode === 'signup') navigate('/onboarding');
      else await loadSub(data.user.id);

    } catch (err) { setApiError(err.message); }
    setIsLoading(false);
  };

  const scrollToPricing = () => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });

  // Styles dynamiques selon thème
  const isClassic = currentTheme === 'classic';
  const accentColor = isClassic ? 'text-emerald-400' : 'text-blue-400';
  const btnClass = isClassic ? 'bg-emerald-700 hover:bg-emerald-800' : 'bg-blue-600 hover:bg-blue-700';

  return (
    <div className="min-h-screen bg-slate-900 font-sans flex flex-col">

      {/* HEADER */}
      <div className="bg-slate-900 shadow-lg sticky top-0 z-50 border-b border-slate-800">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className={`text-2xl font-bold flex items-center gap-2 text-white`}>
            <img src="/France.png" alt="Logo" className="w-8 h-8 object-contain" /> Réglo-veille
          </div>
          <div className="flex items-center gap-4">
            <button onClick={scrollToPricing} className="text-sm font-medium text-gray-300 hover:text-white transition-colors">Tarifs</button>
          </div>
        </div>
      </div>

      {/* HERO SECTION */}
      <div className="flex-grow bg-[#0f172a] relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-blue-500/20 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-[800px] h-[600px] bg-emerald-500/10 rounded-full blur-[100px] pointer-events-none" />

        <div className="max-w-6xl mx-auto px-4 pt-10 pb-20 grid lg:grid-cols-2 gap-16 items-center relative z-10">
          <div className="text-white animate-in slide-in-from-left duration-700">
            <div className="inline-flex items-center gap-2 bg-slate-800/50 border border-slate-700 backdrop-blur-sm px-4 py-1.5 rounded-full text-emerald-400 text-sm font-medium mb-8">
              <CheckCircle className="w-4 h-4" /> Données Officielles Légifrance
            </div>

            <h1 className="text-5xl lg:text-6xl font-extrabold mb-6 leading-tight tracking-tight">
              La Veille Réglementaire <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-blue-500">
                automatisée et simplifiée
              </span>
            </h1>

            <p className="text-lg text-slate-400 mb-6 leading-relaxed max-w-lg">
              <strong className="text-slate-200"> Réglo-veille : L'intelligence artificielle au service de votre veille juridique.</strong>
              Entrez votre <strong className="text-slate-200">code NAF/APE</strong> : nous surveillons Légifrance pour votre activité et vous alertons uniquement si un texte vous touche — comme la réforme de la <strong className="text-slate-200">facturation électronique</strong>.
            </p>

            <p className="text-sm text-slate-500 mb-10 max-w-lg">
              Conçu pour les artisans et TPE/PME : comprendre votre convention collective, valider un contrat, vérifier la conformité de votre site (RGPD, mentions légales) — sans juriste en interne.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <div className="flex items-center gap-3 text-sm font-medium text-slate-300">
                <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-emerald-500 ring-2 ring-slate-700">
                  <Shield size={20} />
                </div>
                <span>Conformité <br /> assurée</span>
              </div>
              <div className="w-px h-10 bg-slate-800 hidden sm:block"></div>
              <div className="flex items-center gap-3 text-sm font-medium text-slate-300">
                <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-blue-500 ring-2 ring-slate-700">
                  <Check size={20} />
                </div>
                <span>Gain de temps <br /> phénoménal</span>
              </div>
            </div>

            <div className="flex flex-col gap-3 text-sm text-slate-400">
              <span className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> Essai gratuit 5 jours sans engagement</span>
              <span className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> Aucune carte bancaire requise</span>
            </div>
          </div>

          {/* AUTH BOX */}
          <div className="relative animate-in slide-in-from-right duration-700 lg:w-[450px] ml-auto">
            <div className="absolute -inset-1 bg-gradient-to-br from-rose-900 via-red-900 to-rose-800 rounded-3xl blur-lg opacity-25" />
            <div className="relative bg-gradient-to-br from-rose-900 via-red-900 to-rose-700 rounded-3xl p-px shadow-2xl shadow-rose-950/30">
              <div className="relative overflow-hidden bg-white rounded-[calc(1.5rem-1px)] p-8">
                <div className="pointer-events-none absolute -top-16 -right-16 w-44 h-44 rounded-full bg-gradient-to-br from-rose-200/40 to-red-200/30 blur-2xl" />
                <div className="pointer-events-none absolute -bottom-20 -left-16 w-48 h-48 rounded-full bg-gradient-to-tr from-rose-200/30 to-orange-100/20 blur-2xl" />

                <div className="relative text-center mb-6">
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-white bg-gradient-to-r from-emerald-600 via-green-600 to-emerald-500 rounded-full px-3 py-1 shadow-md shadow-emerald-900/30">
                    <Sparkles className="w-3.5 h-3.5" /> Essai gratuit
                  </span>
                  <h3 className="mt-3 text-2xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-rose-900 via-red-900 to-rose-700">
                    Démarrer gratuitement
                  </h3>
                  <p className="mt-1.5 text-sm text-slate-500">Créez votre compte en 1 minute</p>
                </div>

            <div className="flex bg-slate-100 p-1 rounded-xl mb-6">
              <button onClick={() => { setAuthMode('login'); setApiError(null); }} className={`flex-1 py-2.5 text-sm font-bold rounded-lg transition-all duration-200 ${authMode === 'login' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}>Connexion</button>
              <button onClick={() => { setAuthMode('signup'); setApiError(null); }} className={`flex-1 py-2.5 text-sm font-bold rounded-lg transition-all duration-200 ${authMode === 'signup' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}>Inscription</button>
            </div>

            {apiError && <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-lg flex items-center gap-2 border border-red-100 animate-in fade-in"><AlertCircle className="w-4 h-4" /> {apiError}</div>}

            <div className="space-y-4">
              {authMode === 'signup' && (
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-500 ml-1">Entreprise</label>
                  <input type="text" placeholder="Nom de votre société" className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-rose-800/20 focus:border-rose-800 outline-none transition-all" value={companyName} onChange={e => setCompanyName(e.target.value)} />
                </div>
              )}
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-500 ml-1">Email professionnel</label>
                <input type="email" placeholder="name@company.com" className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-rose-800/20 focus:border-rose-800 outline-none transition-all" value={email} onChange={e => setEmail(e.target.value)} />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-500 ml-1">Mot de passe</label>
                <div className="relative">
                  <input type={showPassword ? 'text' : 'password'} placeholder="••••••••" className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-rose-800/20 focus:border-rose-800 outline-none transition-all" value={password} onChange={e => setPassword(e.target.value)} />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-3.5 text-slate-400 hover:text-slate-600 transition-colors">{showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button>
                </div>
              </div>

              <button onClick={() => handleAuth(authMode)} disabled={isLoading} className={`w-full py-3.5 rounded-xl font-bold transition-all transform active:scale-[0.98] flex justify-center items-center gap-2 shadow-lg shadow-emerald-900/25 mt-4 text-white bg-gradient-to-r from-emerald-600 via-green-600 to-emerald-500 hover:from-emerald-500 hover:via-green-500 hover:to-emerald-400`}>
                {isLoading ? <Loader className="animate-spin w-5 h-5" /> : (authMode === 'login' ? 'Se connecter' : 'Commencer l\'essai gratuit')}
                {!isLoading && <ArrowRight className="w-4 h-4" />}
              </button>

              {authMode === 'login' && <button onClick={() => navigate('/forgot-password')} className="w-full text-center text-xs text-slate-400 hover:text-rose-800 mt-4 transition-colors">Mot de passe oublié ?</button>}
            </div>

            <div className="mt-6 pt-6 border-t border-slate-100 text-center">
              <p className="text-xs text-slate-400">En vous inscrivant, vous acceptez nos CGU.</p>
            </div>
              </div>
            </div>
          </div>
        </div>

        {/* TRUST SIGNALS */}
        <div className="border-t border-slate-800/50 bg-slate-900/50 backdrop-blur-sm">
          <div className="max-w-6xl mx-auto px-4 py-8 flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-60 grayscale hover:grayscale-0 transition-all duration-500">
            {/* Logos fictifs pour l'exemple "Trusted By"
            <span className="text-xl font-bold text-white flex items-center gap-2"><Shield className="w-6 h-6" /> TECH_CORP</span>
            <span className="text-xl font-bold text-white flex items-center gap-2"><Building className="w-6 h-6" /> IMMEOB</span>
            <span className="text-xl font-bold text-white flex items-center gap-2"><Briefcase className="w-6 h-6" /> CONSULTING & CO</span>
            <span className="text-xl font-bold text-white flex items-center gap-2"><Users className="w-6 h-6" /> HR_SOLUTIONS</span>
            */}
          </div>
        </div>
      </div>

      {/* VALUE PROPOSITION GRID */}
      <div className="bg-slate-50 py-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Pourquoi choisir Réglo-veille ?</h2>
            <p className="text-slate-500 text-lg">Une technologie pensée pour les auto-entrepreneurs, les artisans, dirigeants et les managers qui veulent sécuriser leur activité sans devenir juriste !</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 hover:shadow-xl transition-all hover:-translate-y-1 group">
              <div className="w-14 h-14 bg-emerald-50 rounded-xl flex items-center justify-center text-emerald-600 mb-6 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                <AlertCircle size={28} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Veille ciblée par NAF/APE</h3>
              <p className="text-slate-500 leading-relaxed">Indiquez votre activité : ne recevez que les changements qui vous concernent (ex. facturation électronique pour les commerçants, sécurité chantier pour le BTP). Fini le bruit.</p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 hover:shadow-xl transition-all hover:-translate-y-1 group">
              <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600 mb-6 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                <FileText size={28} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Synthèses claires</h3>
              <p className="text-slate-500 leading-relaxed">Nos algorithmes traduisent le jargon juridique en plans d'actions concrets et compréhensibles en moins de 2 minutes.</p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 hover:shadow-xl transition-all hover:-translate-y-1 group">
              <div className="w-14 h-14 bg-purple-50 rounded-xl flex items-center justify-center text-purple-600 mb-6 group-hover:bg-purple-600 group-hover:text-white transition-colors">
                <CheckCircle size={28} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Mise en conformité</h3>
              <p className="text-slate-500 leading-relaxed">Suivez votre score de conformité, générez vos documents manquants et dormez sur vos deux oreilles.</p>
            </div>
          </div>
        </div>
      </div>

      {/* HOW IT WORKS */}
      <div className="bg-white py-24 border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="order-2 md:order-1">
              {/* Abstract UI representation */}
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-emerald-500 transform rotate-2 rounded-3xl opacity-20 blur-xl"></div>
                <div className="bg-slate-900 rounded-3xl p-6 relative shadow-2xl border border-slate-800 text-slate-300 font-mono text-sm leading-relaxed">
                  <div className="flex gap-2 mb-4">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <p className="text-emerald-400 mb-2">{`> Analyse du décret n°2023-145...`}</p>
                  <p className="mb-2">Impact détecté : <span className="text-white">ÉLEVÉ</span></p>
                  <p className="mb-2">Secteur : <span className="text-white">BTP &amp; Construction</span></p>
                  <p className="mb-4">Résumé : modification des règles de sécurité sur échafaudages...</p>
                  <button onClick={generateMemo} disabled={memoLoading} className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-xs font-sans font-bold hover:bg-emerald-500 transition-colors disabled:opacity-60 flex items-center justify-center gap-2 min-w-[170px]">
                    {memoLoading && <Loader className="w-3.5 h-3.5 animate-spin" />}
                    {memoLoading ? 'Génération...' : 'Générer le mémo équipe'}
                  </button>
                  {memoVisible && (
                    <div className="mt-4 p-4 rounded-lg bg-slate-900 border border-emerald-500/30 animate-in fade-in">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-400 mb-2">Mémo équipe — Décret n°2023-145</p>
                      <p className="text-xs text-slate-300 mb-2">À transmettre : responsable chantier, RH, sécurité</p>
                      <ul className="text-xs text-slate-400 space-y-1.5 mb-3">
                        <li className="flex gap-2"><Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" /> Vérifier les échafaudages en conformité avec la norme NF EN 12811.</li>
                        <li className="flex gap-2"><Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" /> Former les chefs d'équipe aux nouvelles règles de sécurité.</li>
                        <li className="flex gap-2"><Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" /> Mettre à jour le registre de sécurité avant le 15 du mois.</li>
                      </ul>
                      <p className="text-[11px] font-bold text-amber-400">Échéance : 30 jours — action prioritaire</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">L'intelligence artificielle au service de votre tranquillité</h2>
              <div className="space-y-8">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold">1</div>
                  <div>
                    <h4 className="font-bold text-slate-900 text-lg">Connexion aux sources officielles</h4>
                    <p className="text-slate-500">Nous surveillons Légifrance et le Journal Officiel en temps réel pour ne rien rater.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center font-bold">2</div>
                  <div>
                    <h4 className="font-bold text-slate-900 text-lg">Filtrage intelligent</h4>
                    <p className="text-slate-500">Notre IA élimine 95% du bruit pour ne garder que ce qui s'applique à VOTRE entreprise.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center font-bold">3</div>
                  <div>
                    <h4 className="font-bold text-slate-900 text-lg">Alerte &amp; action</h4>
                    <p className="text-slate-500">Recevez une notification simple avec les actions à entreprendre. Pas de blabla, des résultats !</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA BANNER */}
      <div className="bg-slate-900 py-16 border-y border-slate-800">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Prêt à simplifier votre conformité ?</h2>
          <p className="text-slate-400 mb-8 text-lg">Rejoignez les dirigeants qui dorment mieux la nuit : essai gratuit de 5 jours, sans carte bancaire.</p>
          <button onClick={() => { setAuthMode('signup'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="bg-white text-slate-900 px-8 py-4 rounded-full font-bold text-lg hover:bg-emerald-400 transition-colors shadow-lg shadow-emerald-500/20">
            Lancer mon essai gratuit
          </button>
        </div>
      </div>

      {/* TARIFS - (Existing Logic) */}
      <div id="pricing" className="bg-slate-50 py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Des tarifs simples et transparents</h2>
            <p className="text-slate-500">Commencez gratuitement, évoluez selon vos besoins.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 items-start">
            {/* STARTER */}
            <div className="p-8 rounded-2xl border border-slate-200 hover:shadow-lg transition-shadow bg-white">
              <h3 className="text-xl font-bold text-slate-900 mb-2">Starter</h3>
              <div className="text-4xl font-bold text-slate-900 mb-6">79€<span className="text-base font-normal text-slate-500">/mois</span></div>
              <ul className="space-y-3 mb-8 text-sm text-slate-600">
                <li className="flex gap-2"><Check size={16} className="text-emerald-500" /> 12 Thèmes de veille !</li>
                <li className="flex gap-2"><Check size={16} className="text-emerald-500" /> Mise à jour quotidienne</li>
                <li className="flex gap-2"><Check size={16} className="text-emerald-500" /> Support Email</li>
              </ul>
              <button onClick={() => { setAuthMode('signup'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="w-full py-2 rounded-lg border border-slate-300 font-semibold hover:bg-slate-50 text-slate-700">Choisir</button>
            </div>

            {/* PRO */}
            <div className="p-8 rounded-2xl border-2 border-emerald-500 shadow-xl bg-white relative scale-105 z-10">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-emerald-500 text-white px-3 py-1 rounded-full text-xs font-bold uppercase">Populaire</div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Pro</h3>
              <div className="text-4xl font-bold text-slate-900 mb-6">99€<span className="text-base font-normal text-slate-500">/mois</span></div>
              <ul className="space-y-3 mb-8 text-sm text-slate-700 font-medium">
                <li className="flex gap-2"><Check size={16} className="text-emerald-500" /> Tout illimité</li>
                <li className="flex gap-2"><Check size={16} className="text-emerald-500" /> Mise à jour quotidienne</li>
                <li className="flex gap-2"><Check size={16} className="text-emerald-500" /> Assistant IA Standard</li>
                <li className="flex gap-2"><Check size={16} className="text-emerald-500" /> Export PDF</li>
              </ul>
              <button onClick={() => { setAuthMode('signup'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="w-full py-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-lg shadow-emerald-200">Commencer l'essai</button>
            </div>

            {/* ENTERPRISE */}
            <div className="p-8 rounded-2xl border border-slate-200 hover:shadow-lg transition-shadow bg-white">
              <h3 className="text-xl font-bold text-slate-900 mb-2">Enterprise</h3>
              <div className="text-4xl font-bold text-slate-900 mb-6">249€<span className="text-base font-normal text-slate-500">/mois</span></div>
              <ul className="space-y-3 mb-8 text-sm text-slate-600">
                <li className="flex gap-2"><Check size={16} className="text-emerald-500" /> API Dédiée</li>
                <li className="flex gap-2"><Check size={16} className="text-emerald-500" /> IA Avancée</li>
                <li className="flex gap-2"><Check size={16} className="text-emerald-500" /> Audit Annuel</li>
              </ul>
              <button onClick={() => { setAuthMode('signup'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="w-full py-2 rounded-lg border border-slate-300 font-semibold hover:bg-slate-50 text-slate-700">Contacter</button>
            </div>
          </div>
        </div>
      </div>

      {/* FOOTER (Identique Dashboard) */}
      <footer className="bg-slate-900 text-gray-400 py-12 border-t border-slate-800">
        <div className="max-w-6xl mx-auto px-4 text-center text-sm">
          <div className="flex justify-center gap-8 mb-6">
            <button onClick={() => navigate('/mentions-legales')} className="hover:text-white transition-colors">Mentions Légales</button>
            <button onClick={() => navigate('/help')} className="hover:text-white transition-colors">Aide</button>
            <a href="mailto:contact@quest-ia.fr" className="hover:text-white transition-colors">Contact</a>
          </div>
          <p>© {new Date().getFullYear()} Quest-IA.fr - Tous droits réservés</p>
        </div>
      </footer>
    </div>
  );
}